import React, { ChangeEvent, useCallback, useState } from "react";
import { connect } from "react-redux";
import { ApplicationState } from "../store";
import { ThunkDispatch } from "redux-thunk";
import { AnyAction } from "redux";
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { Box, Typography } from "@material-ui/core";
import PrimaryButton from "./PrimaryButton";
import RegisterBusiness from "./RegisterBusiness";
import RegisterPrivate from "./RegisterPrivate";
import { FormattedMessage } from "react-intl";
import { register } from "../store/users/action";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        grow: {
            flexGrow: 1,
        },
        boxLine: {
            border: '1px solid #ebdcc6',
            borderRadius: '4px',
            boxShadow: 'none',
            padding: 10,
            backgroundColor: '#ffffff'
        },
        boxTitle: {
            paddingBottom: 10,
            paddingTop: 10,
            fontWeight: 'bold',
            fontSize: '18px'
        },
        largeTitle: {
            paddingBottom: 15,
            fontWeight: 'bold',
            fontSize: '25px'
        },
        text: {
            fontSize: '14px',
            lineHeight: '25px'
        },
        registrationTypeButtons: {
            display: 'flex',
            justifyContent: 'center',
            gap: '12px',
            flexWrap: 'wrap',
        },
        successBox: {
            maxWidth: 560,
            margin: '32px auto',
            border: '1px solid #ebdcc6',
            borderRadius: '4px',
            padding: theme.spacing(3),
            backgroundColor: '#ffffff',
            textAlign: 'center',
        }
    }),
);

interface PropsFromState {
    registerLoading: boolean;
    registerError: string;
    language: string;
}

interface propsFromDispatch {
    register: (payload: any) => any;
}

type AllProps = PropsFromState & propsFromDispatch;

const Register: React.FC<AllProps> = ({
    language,
    register 
}) => {

    const classes = useStyles();
    const [selected, setSelected] = useState("private");
    const [formState, setFormState] = useState({ selected: "private" })
    const [errors, setErrors] = useState({})
    const [registrationSubmitted, setRegistrationSubmitted] = useState(false);

    const onClick = (selected: string) => {
        setSelected(selected);
        setFormState({ ...formState, selected });
    };

    const handleFormChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.currentTarget;
        setFormState({ ...formState, [name]: value });
    }

    const handleCountryChange = (e: any) => {
        const { name, value } = e.target;
        setFormState({ ...formState, [name]: value });
    }

    const setFormValue = useCallback((name: string, value: any) => {
        setFormState((currentState: any) => {
            const nextState = name === "bulk" ? { ...currentState, ...value } : { ...currentState, [name]: value };
            return JSON.stringify(currentState) === JSON.stringify(nextState) ? currentState : nextState;
        });
    }, []);

    const handleValidation = () => {
        let formData: any = formState;
        let formIsValid = true;
        let error = {};

        //Name
        if (!formData.name) {
            formIsValid = false;
            error = { ...error, "name": "field.empty" }
        }

        if (typeof formData.name !== "undefined") {
            if (!formData.name.match(/^[a-zA-Z]+$/)) {
                formIsValid = false;
                error = { ...error, "name": "field.letters" }
            }
        }

        //Surame
        if (!formData.surname) {
            formIsValid = false;
            error = { ...error, "surname": "field.empty" }
        }

        if (typeof formData.surname !== "undefined") {
            if (!formData.surname.match(/^[a-zA-Z]+$/)) {
                formIsValid = false;
                error = { ...error, "surname": "field.letters" }
            }
        }

        //Email
        if (!formData.email) {
            formIsValid = false;
            error = { ...error, "email": "field.empty" }
        }

        if (typeof formData.email !== "undefined") {
            let lastAtPos = formData.email.lastIndexOf('@');
            let lastDotPos = formData.email.lastIndexOf('.');
            if (!(lastAtPos < lastDotPos && lastAtPos > 0 && formData.email.indexOf('@@') === -1 && lastDotPos > 2 && (formData.email.length - lastDotPos) > 2)) {
                formIsValid = false;
                error = { ...error, "email": "field.emailInvalid" }
            }
        }

        //Phone
        if (!formData.phone) {
            formIsValid = false;
            error = { ...error, "phone": "field.empty" }
        }

        if (typeof formData.phone !== "undefined") {
            if (!formData.phone.match(/^[0-9]+$/)) {
                formIsValid = false;
                error = { ...error, "phone": "field.numbers" }
            }
        }

        //Address
        if (!formData.address) {
            formIsValid = false;
            error = { ...error, "address": "field.empty" }
        }

        //City
        if (!formData.city) {
            formIsValid = false;
            error = { ...error, "city": "field.empty" }
        }

        //Index
        if (!formData.postalCode) {
            formIsValid = false;
            error = { ...error, "postalCode": "field.empty" }
        }

        //Country
        if (!formData.country) {
            formIsValid = false;
            error = { ...error, "country": "field.empty" }
        }

        if (formData.selected === 'business') {

            //Company
            if (!formData.companyName) {
                formIsValid = false;
                error = { ...error, "companyName": "field.empty" }
            }
            //registration number
            if (!formData.companyRegistrationNumber) {
                formIsValid = false;
                error = { ...error, "companyRegistrationNumber": "field.empty" }
            }
            //VAT
            if (!formData.companyVAT) {
                formIsValid = false;
                error = { ...error, "companyVAT": "field.empty" }
            }
            //Position
            if (!formData.position) {
                formIsValid = false;
                error = { ...error, "position": "field.empty" }
            }
        }

        setErrors({ ...error })
        return formIsValid;
    }

    const formSubmit = () => {
        if (handleValidation()) {
            register({
                ...formState,
                legalAddress: {
                    ...(formState as any).legalAddress,
                    postalCode: (formState as any).postalCode,
                },
            }).then((data: any) => {
                if (data?.status === "SUCCESS") {
                    setRegistrationSubmitted(true);
                    window.scrollTo(0, 0);
                }
            }).catch(() => {});
        }
    }

    if (registrationSubmitted) {
        return (
            <Box pt={2} pb={1} pl={6} pr={6}>
                <Box className={classes.successBox}>
                    <Typography component="h1" variant="h5" gutterBottom>
                        <FormattedMessage id="register.successTitle" />
                    </Typography>
                    <Typography>
                        <FormattedMessage id="register.successText" />
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box pt={2} pb={1} pl={6} pr={6}>
            <Box className={classes.largeTitle} textAlign="center">
                <FormattedMessage id="register" />
            </Box>
            <Box className={classes.registrationTypeButtons}>
                <PrimaryButton labelText="register.private" code="private" clickHandler={onClick} buttonEnabled={selected === "business" ? true : false} />
                <PrimaryButton labelText="register.business" code="business" clickHandler={onClick} buttonEnabled={selected === "private" ? true : false} />
            </Box>
            <RegisterBusiness 
                show={selected === "business" ? 'flex' : 'none'} 
                formState={formState} 
                formErrors={errors}
                language={language}
                formChange={handleFormChange} 
                setFormValue={setFormValue}
                selectChange={handleCountryChange} 
                formSubmit={formSubmit} 
            />
            <RegisterPrivate 
                show={selected === "private" ? 'flex' : 'none'} 
                formState={formState} 
                formErrors={errors}
                language={language}
                formChange={handleFormChange} 
                setFormValue={setFormValue}
                selectChange={handleCountryChange} 
                formSubmit={formSubmit} 
            />
        </Box>
    );
};

const mapStateToProps = ({ menu, user, language }: ApplicationState) => ({
    registerLoading: user.registerLoading,
    registerError: user.errors,
    language: language.language,
});

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => {
    return {
        register: (payload: any) => {
          dispatch(register(payload));
        }};
};

export default connect(mapStateToProps, mapDispatchToProps)(Register);
